<template>
  <b-modal
    id="addStructureModal"
    :size="!typeModal ? 'lg' : 'md'"
    :title="$translate('Data')"
    hide-footer
    centered
    content-class="rounded-8"
  >
    <div v-if="typeModal === 'folderName'" class="row">
      <div class="col-12 col-lg-12 col-sm-12 col-xs-12">
        <Input v-model="folderName" form-group-id="newFolderName" placeholder="Folder name" :bottom="30" />
      </div>
      <div class="col-12 col-lg-12 col-sm-12 col-xs-12 d-flex justify-content-between">
        <b-button variant="outline-secondary" size="lg" class="px-65" :disabled="isLoading" @click="onClose">
          {{ 'cancel' | i18n }}
        </b-button>
        <b-button variant="primary" size="lg" class="px-65" :disabled="!folderName || isLoading" @click="() => addNewStructure('folder')">
          {{ 'create' | i18n }}
        </b-button>
      </div>
    </div>
    <div v-else-if="typeModal === 'file'" class="row">
      <div class="col-12 col-lg-12 col-sm-12 col-xs-12">
        <FileInput v-model="file" form-group-id="structureFile" placeholder="File" />
      </div>
      <div class="col-12 col-lg-12 col-sm-12 col-xs-12 d-flex justify-content-between">
        <b-button variant="outline-secondary" size="lg" class="px-65" :disabled="isLoading" @click="onClose">
          {{ 'cancel' | i18n }}
        </b-button>
        <b-button variant="primary" size="lg" class="px-65" :disabled="!file || isLoading" @click="() => addNewStructure('file')">
          {{ 'create' | i18n }}
        </b-button>
      </div>
    </div>
    <div v-else-if="typeModal === 'folder'" class="row">
      <div class="col-12 col-lg-12 col-sm-12 col-xs-12 position-relative">
        <FileInput
          v-model="filesList"
          form-group-id="structureFolder"
          placeholder="Folder"
          directory
          multiple
          no-drop
          :class="{ invisible: isLoading }"
        />
        <div v-if="isLoading" class="position-absolute text-center position-center">
          <b-spinner variant="secondary" />
          <p class="text-secondary">
            {{ 'Files are downloading right now' | i18n }}
          </p>
        </div>
      </div>
      <div class="col-12 col-lg-12 col-sm-12 col-xs-12 d-flex justify-content-between">
        <b-button variant="outline-secondary" size="lg" class="px-65" :disabled="isLoading" @click="onClose">
          {{ 'cancel' | i18n }}
        </b-button>
        <b-button
          variant="primary"
          size="lg"
          class="px-65"
          :disabled="!filesList || isLoading"
          @click="() => addNewStructure('folder content')"
        >
          {{ 'create' | i18n }}
        </b-button>
      </div>
    </div>
    <div v-else class="row">
      <div class="col-12 col-lg-4 col-sm-12 col-xs-12">
        <div class="text-center button-wrapper cursor-pointer" @click="typeModal = 'folderName'">
          <UploadFolderIcon class="mt-4" />
          <p class="mt-1 mb-4">
            {{ 'Add Folder' | i18n }}
          </p>
        </div>
      </div>
      <div class="col-12 col-lg-4 col-sm-12 col-xs-12 text-center">
        <div class="text-center button-wrapper cursor-pointer" @click="typeModal = 'file'">
          <UploadFileIcon class="mt-4" />
          <p class="mt-1 mb-4">
            {{ 'Add File' | i18n }}
          </p>
        </div>
      </div>
      <div class="col-12 col-lg-4 col-sm-12 col-xs-12 text-center">
        <div class="text-center button-wrapper cursor-pointer" @click="typeModal = 'folder'">
          <UploadFullFolderIcon class="mt-4" />
          <p class="mt-1 mb-4">
            {{ 'Upload Folder' | i18n }}
          </p>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import UploadFileIcon from '../../../../assets/icons/iconsCode/upload-file';
import UploadFolderIcon from '../../../../assets/icons/iconsCode/upload-folder';
import UploadFullFolderIcon from '../../../../assets/icons/iconsCode/upload-full-folder';
import Input from '../../common/input';
import FileInput from '../../common/fileInput';
import api from '../../../api';
import { NotificationMixin } from '../../../../mixins';

export default {
  name: 'AddStructureModal',
  components: { UploadFileIcon, UploadFolderIcon, Input, FileInput, UploadFullFolderIcon },
  mixins: [NotificationMixin],
  props: {
    parentFolderId: {
      type: String,
      default: null,
    },
    parentChildListLength: {
      type: Number,
      default: null,
    },
    updateGeneralStructure: {
      type: Function,
      default: () => {},
    },
    getFullStructure: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      typeModal: '',
      folderName: '',
      file: null,
      filesList: null,
      isLoading: false,
    };
  },
  methods: {
    onResetData() {
      this.typeModal = '';
      this.folderName = '';
      this.file = null;
      this.filesList = null;
      this.isLoading = false;
    },
    onClose() {
      this.$root.$emit('bv::hide::modal', 'addStructureModal');
      this.onResetData();
    },
    createFolderStructure(array) {
      const root = {};
      
      array.forEach((file) => {
        const { $path: path } = file;
        const parts = path.split('/');
        let currentFolder = root;

        for (let i = 0; i < parts.length - 1; i++) {
          const folderName = parts[i];
          if (!currentFolder[folderName]) {
            currentFolder[folderName] = {};
          }
          currentFolder = currentFolder[folderName];
        }

        const fileName = parts[parts.length - 1];
        currentFolder[fileName] = { file };
      });

      return root;
    },
    async createFolderOrFileRecursive({ structure, parentFolderId, firstOrderNumber }) {
      for (const itemName in structure) {
        if (structure.hasOwnProperty.call(structure, itemName)) {
          const item = structure[itemName];
          const formData = new FormData();

          if (item.hasOwnProperty.call(item, 'file')) {
            // It's a file
            const file = item.file;
            formData.append('file', file);
            formData.append('name', itemName);
            formData.append('type', 'file');
          } else {
            // It's a folder
            formData.append('name', itemName);
            formData.append('type', 'folder');
          }

          if (firstOrderNumber) {
            formData.append('orderNumber', firstOrderNumber);
          }
          formData.append('owned', parentFolderId);

          const createdFolderData = await api.viewStructure.createStructure(formData);

          // Recursively create the content of the folder
          if (typeof item === 'object' && !item.hasOwnProperty.call(item, 'file')) {
            await this.createFolderOrFileRecursive({ structure: item, parentFolderId: createdFolderData.id });
          }
        }
      }
    },
    async addNewStructure(structureType) {
      try {
        this.isLoading = true;
        if (structureType === 'folder content') {
          const files = this.filesList.filter((file) => file.type);
          const folderStructure = this.createFolderStructure(files);
          await this.createFolderOrFileRecursive({
            structure: folderStructure,
            parentFolderId: this.parentFolderId,
            firstOrderNumber: this.parentChildListLength + 1,
          });
          await this.getFullStructure();
        } else {
          const formData = new FormData();
          if (structureType === 'folder') {
            formData.append('name', this.folderName);
            formData.append('type', 'folder');
          } else if (structureType === 'file') {
            formData.append('file', this.file);
            formData.append('name', this.file.name);
            formData.append('type', 'file');
          }
          formData.append('orderNumber', this.parentChildListLength + 1);
          formData.append('owned', this.parentFolderId);
          const createdFolderData = await api.viewStructure.createStructure(formData);
          this.updateGeneralStructure(createdFolderData);
        }
        this.showNotification({
          type: 'success',
          message: `${structureType.charAt(0).toUpperCase() + structureType.slice(1)} has been successfully added.`,
        });
        this.onClose();
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.button-wrapper {
  border: 1px solid rgba(197, 208, 222, 0.4);
  border-radius: 8px;
  box-shadow: 4px 0px 16px rgba(15, 32, 65, 0.04);
}
.px-65 {
  padding-left: 65px;
  padding-right: 65px;
}
.position-center {
  top: 50%;
  left: 50%;
  transform: translate(-50%,-50%);
}
::v-deep .modal-header {
  border: none;
  padding-bottom: 0px !important;
}
::v-deep .rounded-8 {
  border-radius: 8px;
}
::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}
</style>
